"use client";

import type { MouseEvent } from "react";

import { getChainConfig } from "@/config/chains/helpers";

import { dispatchCustomEvent } from "./dispatch-custom-event";
import { type ResolverEntryMap, resolveValues } from "./resolvers";
import type { DisjointProperties } from "./type-helpers";

type EventType =
  | "product.clicked"
  | "product.hovered"
  | "product.experiment.viewed"
  | "product.form.attempted"
  | "product.form.interacted"
  | "product.form.observed"
  | "product.form.submitted"
  | "product.form.updated"
  | "product.pageview"
  | "product.viewed"
  | "product.verification.updated"
  | "product.video.interacted"
  | "product.error.captured"
  | "product.success.captured"
  | "product.wallet.updated";

type EventAction =
  // Links
  | "follow"
  | "interact"
  | "call-to-action"
  | "back"

  // Specific Interactions
  | "modal.opened"
  | "modal.closed"
  | "dropdown.opened"
  | "dropdown.closed"
  | "toggle.expanded"
  | "toggle.collapsed"
  | "tooltip.opened"
  | "tooltip.closed"
  | "hover-card.opened"
  | "hover-card.closed"
  | "banner.dismissed"

  // Video
  | "video.started"
  | "video.played"
  | "video.paused"
  | "video.ended"
  | "video.watched-10"
  | "video.watched-25"
  | "video.watched-50"
  | "video.watched-75"

  // Accounts
  //// Bank Accounts
  | "bank-account.added"
  | "bank-account.removed"
  | "bank-account.nickname-edited"
  //// OTC Addresses (Scheduled Settlement) aka Recipient Addresses // | 'otc-address'
  //// Instant Addresses (Instant Settlement) aka Atlas Addresses // | 'instant-address'
  | "blockchain-address.added"
  | "blockchain-address.removed"
  | "blockchain-address.nickname-edited"

  // Redeem Connections
  | "redeem-connection.added"
  | "redeem-connection.removed"
  | "redeem-connection.nickname-edited"

  // Routes
  | "mint-route.added"
  | "redeem-route.added"

  // Issuances
  | "white-label-ausd.added"
  | "white-label-ausd.inquiry"

  // Rewards Payout
  //// One action for both the first setup and a later change; `outcome` tells them apart.
  | "reward-payout.set"

  // Swap Pair Management (Internal Admin)
  | "internal.swap-pair.added-liquidity"
  | "internal.swap-pair.removed-liquidity"
  | "internal.swap-pair.set-price"
  | "internal.swap-pair.updated-fees"
  | "internal.swap-pair.transferred-fees"
  | "internal.swap-pair.verified-address"

  // Agreements
  //// From Sign Up Flow
  | "mint-agreement.accepted"
  | "redeem-agreement.accepted"
  //// From New Agreement Upload
  | "terms-of-service.reaccepted"
  | "privacy-policy.reaccepted"

  // Sign Up Flow
  | "sign-up-flow.legal-consent-granted"

  // Application onboarding
  | "onboarding.account-use.selected"
  | "onboarding.application.started"
  | "onboarding.application.submitted"
  | "onboarding.company-basics.provided"
  | "onboarding.company-documents.completed"
  | "onboarding.company-info.completed"
  | "onboarding.document-ocr.parsed"
  | "onboarding.entity-type.selected"
  | "onboarding.follow-up-questions.completed"
  | "onboarding.formation-document.uploaded"
  | "onboarding.ownership-details.completed"
  | "onboarding.person.saved"
  | "onboarding.primary-volume.selected"
  | "onboarding.products.selected"
  | "onboarding.rewards-recipients.selected"

  // Team Members
  | "invite-team-member.succeeded"

  // Verification
  | "message-received"

  // Auth
  | "auth.login.succeeded"
  | "auth.logout.succeeded"
  | "auth.screen.entered"
  | "auth.screen.errored"

  // Error
  | "error.auth.required"
  | "error.auth.failed"
  | "error.auth.upstream-unauthorized"
  | "error.onboarding.document-ocr"
  | "error.onboarding.upload"
  | "error.query-cache.unknown"
  | "error.mutation-cache.stale-build"
  | "error-boundary.unknown"
  | "error-boundary.connection-dropped"

  // Wallet
  | "wallet.chain-updated"
  | "wallet.connect-intended"
  | "wallet.connected"
  | "wallet.disconnected"
  | "wallet.gas-updated"

  // Embedded Wallet (Turnkey)
  | "embedded-wallet.account-added"
  | "embedded-wallet.add-signer"
  | "embedded-wallet.remove-signer"
  | "embedded-wallet.add-account-continued"
  | "embedded-wallet.education-continued"
  | "embedded-wallet.lookup"
  | "embedded-wallet.login"
  | "embedded-wallet.recovery-login"
  | "embedded-wallet.signer-enroll"
  | "embedded-wallet.login-rules"
  | "embedded-wallet.sms-recovery-offer"
  | "embedded-wallet.sms-recovery-setup"
  | "embedded-wallet.sms-recovery-off"
  | "embedded-wallet.sms-recovery-number"
  | "embedded-wallet.otp-send"
  | "embedded-wallet.otp-verify"
  | "embedded-wallet.passkey-created"
  | "embedded-wallet.created"
  | "embedded-wallet.backup-revealed"
  | "embedded-wallet.backup-acknowledged"
  | "embedded-wallet.sync"
  | "embedded-wallet.view-wallets"
  | "embedded-wallet.send-prepare"
  | "embedded-wallet.send-sign"
  | "embedded-wallet.send-broadcast"
  | "embedded-wallet.auth-error"

  // Admin
  | "admin.swap-pair-management-action.logged";

type EventLocation = "body" | "global" | "header" | "iframe" | "modal" | "sidebar";

type Key = "auto" | string;

type ElementHref = string;

type ElementKey = string;

type ElementText = string;

export type MetadataFormValues = Record<string, string | string[]>;

export type AgoraEventDetails = Record<string, unknown>;

type AgoraEventPropsWithoutMetadata = {
  elementHref?: ElementHref;
  elementKey?: ElementKey;
  elementText?: ElementText;
  elementTextAuto?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>;
  eventAction?: EventAction;
  eventKey?: Key;
  eventLocation: EventLocation;
  eventType: EventType;
};

type AgoraEventPropsWithoutMetadataKeys = keyof AgoraEventPropsWithoutMetadata;

type ConstrainedAgoraEventDetails = {
  formRawValues?: MetadataFormValues;
  formResolvedValues?: MetadataFormValues;
  formSessionId?: string;
  chainId?: string | number;
  response?: MetadataFormValues | MetadataFormValues[];
} & DisjointProperties<AgoraEventDetails, Record<AgoraEventPropsWithoutMetadataKeys, AgoraEventDetails>>;

export type DispatchAgoraEventProps = AgoraEventPropsWithoutMetadata & {
  /**
   * Event-specific details (dimensions) like result, variant, etc.
   * NOT page metadata (title, description, etc.).
   * AND not the same as the other keys.
   */
  eventDetails?: ConstrainedAgoraEventDetails;
};

export const dispatchAgoraEventName = "__agora_dispatch_event" as const;

declare global {
  interface WindowEventMap {
    __agora_dispatch_event: CustomEvent<DispatchAgoraEventProps>;
  }
}

/**
 * dispatchAgoraEvent
 * @returns window.dispatchEvent(
    new CustomEvent('__agora_dispatch_event', {
      detail: {
        elementText,
        eventAction,
        elementHref,
        eventKey,
        eventType,
        eventLocation,
      },
    }),
  )
 * @example
 *  onClick={()=> dispatchAgoraEvent({
      eventAction: 'follow',
      eventType: 'product.clicked',
      elementText: 'Accounts'
      elementHref: '/a/c/accounts',
      eventKey: 'auto',
      eventLocation: 'body',
    })}
 */
export function dispatchAgoraEvent(props: DispatchAgoraEventProps) {
  const { eventKey = "auto", eventDetails, elementTextAuto, ...rest } = props;

  let finalEventDetails: AgoraEventDetails | undefined = eventDetails;

  if (finalEventDetails) {
    const resolvedValues = resolveValues(finalEventDetails, METADATA_RESOLVERS);

    if (resolvedValues) {
      finalEventDetails = {
        ...finalEventDetails,
        ...resolvedValues,
      } as AgoraEventDetails;
    }
  }

  return dispatchCustomEvent(dispatchAgoraEventName, {
    eventKey,
    // biome-ignore lint/performance/useDomNodeTextContent: analytics should record the visible text, not hidden markup
    ...(elementTextAuto ? { elementText: elementTextAuto.currentTarget.innerText } : {}),
    ...(finalEventDetails ? { eventDetails: finalEventDetails } : {}),
    ...rest,
  });
}

const METADATA_RESOLVERS: ResolverEntryMap = {
  chainId: {
    fn: resolveChainId,
    outputKey: "chainName",
  },
};

function resolveChainId(chainId: unknown) {
  return getChainConfig(Number(chainId))?.name;
}
